import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import './scss/GitHubCallback.scss'; // Reuse existing callback styles

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Validated original domain, captured for the error-redirect path. The
      // oauth_state_data is removed mid-flow (before the auth fetch), so the
      // catch block can't re-read it — use this instead.
      let errorRedirectDomain = null;
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const stateParam = searchParams.get('state');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        if (!stateParam) {
          throw new Error('No state parameter received');
        }

        // Parse state data - handle both old JSON format and new simple format
        let stateData;
        try {
          // Try new simple format first: "state|base64Domain|provider|action"
          if (stateParam.includes('|')) {
            const parts = stateParam.split('|');
            if (parts.length === 4) {
              stateData = {
                state: parts[0],
                originalDomain: atob(parts[1]),
                provider: parts[2],
                action: parts[3]
              };
            } else {
              throw new Error('Invalid simple state format');
            }
          } else {
            // Fallback to old JSON format
            stateData = JSON.parse(decodeURIComponent(stateParam));
          }
        } catch (e) {
          throw new Error('Invalid state parameter format');
        }

        const { state, provider, action } = stateData;
        let { originalDomain } = stateData;

        if (!state || !originalDomain || !provider || !action) {
          throw new Error('Missing required state data');
        }

        // Validate originalDomain to prevent open redirect attacks
        try {
          const domainUrl = new URL(originalDomain);
          const isAllowed = domainUrl.hostname === 'zoobbe.com' ||
            domainUrl.hostname.endsWith('.zoobbe.com') ||
            domainUrl.hostname === 'localhost' ||
            domainUrl.hostname === '127.0.0.1';
          if (!isAllowed) {
            originalDomain = window.location.origin;
          }
        } catch {
          originalDomain = window.location.origin;
        }

        // Remember the validated domain for the error-redirect path below.
        errorRedirectDomain = originalDomain;

        // Verify state matches stored data
        const storedStateData = localStorage.getItem('oauth_state_data');
        if (!storedStateData) {
          throw new Error('No stored state data found');
        }

        const storedData = JSON.parse(storedStateData);
        if (storedData.state !== state) {
          throw new Error('State mismatch - possible CSRF attack');
        }

        // Clean up stored data
        localStorage.removeItem('oauth_state_data');

        setMessage('Authenticating with server...');

        // Call the appropriate backend endpoint
        const endpoint = provider === 'github' ? '/v1/users/auth/github' : '/v1/users/auth/google';
        const authResponse = await fetch(config.API_URI + endpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            code: code,
            action: action,
            originalDomain: originalDomain
          }),
        });

        if (!authResponse.ok) {
          const errorData = await authResponse.json();
          throw new Error(errorData.message || 'Authentication failed');
        }

        await authResponse.json();
        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        // Get user data
        const userResponse = await fetch(config.API_URI + '/v1/users/me', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          const username = userData.user.username;

          // Honor a safe returnTo deep-link saved before the OAuth round-trip
          // (e.g. from a join link); otherwise go to the user's boards.
          const safeReturnTo = (() => {
            const rt = sessionStorage.getItem('returnTo');
            if (!rt || !rt.startsWith('/') || rt.startsWith('//')) return null;
            const safePrefixes = ['/u/', '/w/', '/b/', '/c/', '/cli/auth', '/workspace', '/notifications', '/import', '/whats-new'];
            return safePrefixes.some(p => rt.startsWith(p)) ? rt : null;
          })();
          if (safeReturnTo) sessionStorage.removeItem('returnTo');

          // Redirect back to the original domain
          setTimeout(() => {
            window.location.href = safeReturnTo
              ? `${originalDomain}${safeReturnTo}`
              : `${originalDomain}/u/${username}/boards`;
          }, 1000);
        } else {
          // Fallback redirect to original domain home
          setTimeout(() => {
            window.location.href = originalDomain;
          }, 1000);
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed');

        // Redirect to the validated original domain captured before the
        // oauth_state_data was removed; fall back to the main domain otherwise.
        const redirectBase = errorRedirectDomain || config.DOMAIN;
        setTimeout(() => {
          window.location.href = `${redirectBase}/login?error=${encodeURIComponent(error.message)}`;
        }, 2000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="github-callback-container">
      <div className="github-callback-content">
        <div className="github-callback-spinner">
          <Spinner color='var(--brand-color)' size={40} strokeWidth={3} />
        </div>
        <h2>
          {status === 'processing' && 'Processing Authentication'}
          {status === 'success' && 'Authentication Successful'}
          {status === 'error' && 'Authentication Failed'}
        </h2>
        <p>{message}</p>
        {status === 'error' && (
          <p className="github-callback-error-note">
            You will be redirected back to the login page shortly.
          </p>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
